import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { LOGO_FULL_TRANSPARENT_URL, SERVICES, CONTACT, ILLUS_HERO, ABOUT_IMAGE } from "@/lib/constants";
import { SocialIcons } from "@/components/SocialIcons";
import { MessageCircle, Send, MapPin, ChevronRight } from "lucide-react";
import { apiFetch } from "@/lib/api";

/* @section: home */
export default function HomePage() {
  const [sections, setSections] = useState<Record<string, boolean>>({});
  const [content, setContent] = useState<Record<string, string>>({});
  const [images, setImages] = useState<Record<string, string>>({});

  useEffect(() => {
    apiFetch("/content/sections").then((r) => r.json()).then((d) => setSections(d.data ?? {})).catch(() => {});
    apiFetch("/content/text").then((r) => r.json()).then((d) => setContent(d.data ?? {})).catch(() => {});
    apiFetch("/content/images").then((r) => r.json()).then((d) => setImages(d.data ?? {})).catch(() => {});
  }, []);

  const logoUrl = images["hero_logo"] ?? LOGO_FULL_TRANSPARENT_URL;

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navbar />

      {/* ── Hero ── */}
      {!sections["hero"] && (
        <section className="relative min-h-screen bg-[#2E8B57] flex items-center overflow-hidden pt-16">
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: "radial-gradient(ellipse at 20% 60%, #FFE000 0%, transparent 50%), radial-gradient(ellipse at 80% 20%, #FFE000 0%, transparent 40%)"
          }} />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="mb-6">
                <img src={logoUrl} alt="YoYo Massage Wellness" style={{ width: "100%", maxWidth: "520px", height: "auto", display: "block" }} />
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                {content["hero_title"] ?? "專業養生按摩"}<br />
                <span className="text-[#FFE000]">{content["hero_title2"] ?? "身心靈全面調理"}</span>
              </h1>
              <p className="text-gray-300 text-lg mb-8 leading-relaxed max-w-lg">
                {content["hero_subtitle"] ?? "結合傳統東方智慧與現代保健技術，為您帶來深層放鬆的全面調理。"}
              </p>
              <div className="flex flex-wrap gap-3">
                <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer"
                   className="flex items-center gap-2 px-6 py-3 bg-[#25D366] text-white font-semibold rounded-full hover:bg-[#1ebe5b] transition-all hover:scale-105 shadow-lg">
                  <MessageCircle size={18} /> WhatsApp 預約
                </a>
                <a href={CONTACT.telegram} target="_blank" rel="noopener noreferrer"
                   className="flex items-center gap-2 px-6 py-3 bg-[#2AABEE] text-white font-semibold rounded-full hover:bg-[#1a9fd4] transition-all hover:scale-105 shadow-lg">
                  <Send size={18} /> Telegram 預約
                </a>
              </div>
              <SocialIcons className="mt-4" />
              <div className="flex items-center gap-2 mt-5 text-gray-400 text-sm">
                <MapPin size={14} className="text-[#FFE000]" /> {CONTACT.location}
              </div>
            </div>
            <div className="hidden lg:flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-[#FFE000]/15 rounded-3xl blur-3xl" />
                <img src={images["hero_illustration"] ?? ILLUS_HERO} alt="養生按摩"
                     className="relative rounded-3xl shadow-2xl border border-[#FFE000]/20 max-w-md w-full object-cover"
                     style={{ animation: "float 6s ease-in-out infinite" }} />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── Services Grid ── */}
      {!sections["services"] && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#2E8B57] mb-3">
                {content["services_title"] ?? "我們的服務"}
              </h2>
              <div className="w-16 h-1 bg-[#FFE000] mx-auto mb-4" />
              <p className="text-gray-600 max-w-2xl mx-auto">
                {content["services_subtitle"] ?? "融合傳統東方養生智慧與現代保健技術，五大專業項目為您提供全面身心調理。"}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {SERVICES.map((s) => (
                <Link key={s.id} to={s.slug}
                  className="group bg-white rounded-2xl overflow-hidden shadow-md border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                  <div className="relative h-48 overflow-hidden bg-white">
                    <img src={images[`illus_${s.id}`] ?? s.illustration} alt={s.name}
                         className="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute top-3 right-3 w-11 h-11 rounded-full overflow-hidden border-2 border-[#FFE000] bg-white shadow-lg">
                      <img src={images[`icon_${s.id}`] ?? s.icon} alt={s.name} className="w-full h-full object-cover" style={{ mixBlendMode: "multiply" }} />
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-[#2E8B57] font-bold text-lg mb-1">{s.name}</h3>
                    <p className="text-gray-500 text-xs mb-3 leading-relaxed">{s.subtitle}</p>
                    <p className="text-gray-600 text-sm leading-relaxed line-clamp-2 mb-4">{s.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400 bg-gray-50 px-2 py-1 rounded-full">⏱ {s.duration}</span>
                      <span className="flex items-center gap-1 text-[#FFE000] font-semibold text-sm group-hover:gap-2 transition-all">
                        了解更多 <ChevronRight size={15} />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Gallery Showcase ── */}
      {!sections["showcase"] && (
        <section className="py-20 bg-[#2E8B57]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
                {content["showcase_title"] ?? "養生體驗一覽"}
              </h2>
              <div className="w-16 h-1 bg-[#FFE000] mx-auto" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {SERVICES.map((s) => (
                <Link key={s.id} to={s.slug} className="relative rounded-xl overflow-hidden group cursor-pointer">
                  <img src={images[`illus_${s.id}`] ?? s.illustration} alt={s.name}
                       className="w-full h-44 object-cover group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2E8B57] via-transparent to-transparent" />
                  <div className="absolute bottom-2 left-2 right-2">
                    <p className="text-[#FFE000] font-bold text-sm">{s.name}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── About ── */}
      {!sections["about"] && (
        <section id="about" className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-12 h-1 bg-[#FFE000] mb-4" />
              <h2 className="text-3xl sm:text-4xl font-bold text-[#2E8B57] mb-6">
                {content["about_title"] ?? "關於 YoYo Massage Wellness"}
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6 text-lg">
                {content["about_text"] ?? "YoYo Massage Wellness 位於香港尖沙咀，專注於提供高品質的養生按摩服務。Yoyo調理師精通多項傳統與現代按摩技法，以細心、專業的態度，為每位顧客量身訂造最適合的保健方案。"}
              </p>
              <ul className="space-y-3">
                {["專業手法細心到位","融合傳統東方養生與現代保健","私密舒適的保健環境","量身訂造個人化保健方案"].map((p, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#FFE000] flex items-center justify-center flex-shrink-0">
                      <span className="text-[#2E8B57] text-xs font-bold">✓</span>
                    </div>
                    <span className="text-gray-700">{content[`about_point${i+1}`] ?? p}</span>
                  </li>
                ))}
              </ul>

              {/* Blog & Reviews links */}
              <div className="flex flex-wrap gap-3 mt-8">
                <a href="/#/blog"
                  className="flex items-center gap-2 px-5 py-2.5 bg-[#2E8B57] text-white font-semibold rounded-xl hover:bg-[#1C6034] transition-colors shadow-sm">
                  📝 養生博客
                </a>
                <a href="/#/reviews"
                  className="flex items-center gap-2 px-5 py-2.5 bg-[#FFE000] text-[#0A1628] font-semibold rounded-xl hover:bg-yellow-300 transition-colors shadow-sm">
                  ⭐ 客戶評價
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-[#FFE000]/10 rounded-3xl" />
              <img src={images["about_image"] ?? ABOUT_IMAGE} alt="關於我們"
                   className="relative rounded-2xl shadow-xl w-full object-cover" />
            </div>
          </div>
        </section>
      )}

      {/* ── Contact ── */}
      {!sections["contact"] && (
        <section className="py-20 bg-[#2E8B57]">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              {content["contact_title"] ?? "立即預約，開啟您的養生之旅"}
            </h2>
            <div className="w-16 h-1 bg-[#FFE000] mx-auto mb-6" />
            <p className="text-gray-400 mb-8 text-lg">
              {content["contact_subtitle"] ?? "透過 WhatsApp 或 Telegram 聯絡我們安排預約"}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer"
                 className="flex items-center justify-center gap-3 px-8 py-4 bg-[#25D366] text-white font-bold text-lg rounded-2xl hover:bg-[#1ebe5b] transition-all hover:scale-105 shadow-xl">
                <MessageCircle size={22} /> WhatsApp 預約
              </a>
              <a href={CONTACT.telegram} target="_blank" rel="noopener noreferrer"
                 className="flex items-center justify-center gap-3 px-8 py-4 bg-[#2AABEE] text-white font-bold text-lg rounded-2xl hover:bg-[#1a9fd4] transition-all hover:scale-105 shadow-xl">
                <Send size={22} /> Telegram 預約
              </a>
            </div>
            <div className="flex flex-wrap justify-center gap-6 text-gray-400 text-sm">
              <a href={CONTACT.tgChannel} target="_blank" rel="noopener noreferrer"
                 className="flex items-center gap-2 hover:text-[#2AABEE] transition-colors">
                <Send size={15} /> 關注 TG Channel
              </a>
              <span className="flex items-center gap-2">
                <MapPin size={15} className="text-[#FFE000]" /> {CONTACT.location}
              </span>
            </div>
          </div>
        </section>
      )}

      <Footer />
      <style>{`@keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-12px)}}`}</style>
    </div>
  );
}
